import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { ApiError } from "../api/client";
import {
  errorMessage,
  useConsoleConfig,
  useDataset,
  useUpdateDataset,
  useVersions,
} from "../api/queries";
import { useSession } from "../auth/SessionProvider";
import { isStorageProblem, StorageProblem } from "../components/StorageProblem";
import { BarSection } from "../components/dataset/BarSection";
import { ConsentSection } from "../components/dataset/ConsentSection";
import { DealGate } from "../components/dataset/DealGate";
import { DeletionSection } from "../components/dataset/DeletionSection";
import { EventTrace } from "../components/dataset/EventTrace";
import { FetchBlock } from "../components/dataset/FetchBlock";
import { VersionList } from "../components/dataset/VersionList";
import { AddFile } from "../components/upload/AddFile";
import { PageHeader } from "../components/layout/PageHeader";
import { Field } from "../components/ui/Field";
import { ErrorBlock, LoadingBlock } from "../components/ui/StateBlock";
import { TASK_TYPE_LABELS } from "../lib/metrics";

/**
 * Correct the declared target column.
 *
 * A declared field is correctable precisely because it was declared; the
 * correction is written into the trace carrying the old value rather than
 * overwriting history quietly.
 */
function CorrectTargetColumn({
  datasetId,
  current,
  onDone,
}: {
  datasetId: string;
  current: string | null;
  onDone: () => void;
}): JSX.Element {
  const fieldId = `target-column-${datasetId}`;
  const [value, setValue] = useState(current ?? "");
  const update = useUpdateDataset(datasetId);

  return (
    <form
      className="form"
      onSubmit={(event) => {
        event.preventDefault();
        update.mutate(
          { target_column: value.trim() ? value.trim() : null },
          { onSuccess: onDone },
        );
      }}
    >
      <Field
        label="The column that holds the answer"
        htmlFor={fieldId}
        hint="What the client told you it is called. The console checks it against each file and never rejects one for it."
        error={update.isError ? errorMessage(update.error) : undefined}
      >
        <input
          id={fieldId}
          className="input input--mono"
          value={value}
          onChange={(event) => setValue(event.target.value)}
        />
      </Field>
      <div className="gate__actions">
        <button
          type="submit"
          className="btn btn--primary btn--sm"
          disabled={update.isPending}
        >
          {update.isPending ? "Correcting…" : "Correct it"}
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={onDone}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

/**
 * One dataset: the deal gate, the consent, the files, the lines, the trace.
 *
 * The order is the order somebody reads it in — where the deal stands, what we
 * are allowed to do, what we actually hold, how to get it, and what happened.
 *
 * Returns:
 *     The dataset page.
 */
export function DatasetPage(): JSX.Element {
  const { id = "" } = useParams();
  const { me } = useSession();
  const dataset = useDataset(id);
  const versions = useVersions(id);
  const config = useConsoleConfig();
  const [correcting, setCorrecting] = useState(false);

  const header = (
    <PageHeader
      eyebrow="Dataset"
      title={dataset.data ? `${dataset.data.account.name} · ${dataset.data.name}` : "Dataset"}
      back={{ to: "/", label: "All clients" }}
      subtitle={
        dataset.data
          ? `${TASK_TYPE_LABELS[dataset.data.task_type]} · added by ${dataset.data.owner}`
          : undefined
      }
      actions={
        dataset.data ? (
          <Link className="btn btn--sm" to={`/datasets/${id}/reads`}>
            Who used this
          </Link>
        ) : undefined
      }
    />
  );

  if (dataset.isPending) {
    return (
      <>
        {header}
        <LoadingBlock label="Reading the record…" />
      </>
    );
  }

  if (isStorageProblem(dataset.error)) {
    return (
      <>
        {header}
        <StorageProblem
          error={dataset.error}
          onRetry={() => void dataset.refetch()}
        />
      </>
    );
  }

  if (dataset.isError || !dataset.data) {
    const error = dataset.error;
    if (
      error instanceof ApiError &&
      (error.isNotFound || error.isForbidden)
    ) {
      return (
        <>
          {header}
          <p className="state-block">{error.message}</p>
        </>
      );
    }
    return (
      <>
        {header}
        <ErrorBlock error={error} onRetry={() => void dataset.refetch()} />
      </>
    );
  }

  const record = dataset.data;
  const latest = record.versions.latest;
  const permitted = record.consent.permissions;

  return (
    <>
      {header}

      <DealGate dataset={record} config={config.data} />

      <ConsentSection dataset={record} config={config.data} />

      <section className="section">
        <div className="section__head">
          <h2 className="section__title">What was declared</h2>
          {record.deleted_at || correcting ? null : (
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => setCorrecting(true)}
            >
              Correct the target column
            </button>
          )}
        </div>
        {correcting ? (
          <CorrectTargetColumn
            datasetId={id}
            current={record.target_column}
            onDone={() => setCorrecting(false)}
          />
        ) : (
          <p className="section__lead">
            {TASK_TYPE_LABELS[record.task_type]}
            {record.target_column ? (
              <>
                , and the answer is in{" "}
                <span className="mono">{record.target_column}</span>. Whether
                that column is actually in a file is measured per file, below.
              </>
            ) : (
              <>
                . Nobody has said which column holds the answer yet — that is a
                state, and you can correct it whenever the client tells you.
              </>
            )}
          </p>
        )}
      </section>

      <section className="section">
        <div className="section__head">
          <h2 className="section__title">The files</h2>
          <span className="section__meta">
            {record.versions.available} of {record.versions.total} available
          </span>
        </div>
        {versions.isPending ? (
          <LoadingBlock label="Reading the files…" />
        ) : isStorageProblem(versions.error) ? (
          <StorageProblem
            error={versions.error}
            onRetry={() => void versions.refetch()}
          />
        ) : versions.isError ? (
          <ErrorBlock
            error={versions.error}
            onRetry={() => void versions.refetch()}
          />
        ) : (
          <VersionList
            versions={versions.data.items}
            datasetId={id}
            declaredTargetColumn={record.target_column}
            permitted={permitted}
          />
        )}
        {record.deleted_at === null ? (
          // Three screens tell somebody to "start the file again on the
          // dataset's page". This is that place.
          <AddFile
            datasetId={id}
            datasetLabel={`${record.account.name} · ${record.name}`}
          />
        ) : null}
      </section>

      {latest ? (
        <FetchBlock
          datasetId={id}
          versionNumber={latest.number}
          permitted={permitted}
          enabled={latest.state === "available"}
        />
      ) : null}

      <BarSection dataset={record} config={config.data} />

      <DeletionSection dataset={record} me={me} />

      <section className="section">
        <div className="section__head">
          <h2 className="section__title">What happened</h2>
          <span className="section__meta">the console's own words</span>
        </div>
        <EventTrace events={record.events} />
      </section>
    </>
  );
}
